"use client";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { signOut, User } from "firebase/auth";
import { doc, onSnapshot, Timestamp } from "firebase/firestore";
import { auth, db } from "@/firebase/firebase";
import {
  ensureAnonymousFirebaseUser,
  startFirebaseIdTokenCookieSync,
} from "@/utils/firebaseSession";

const RECOVERABLE_AUTH_ERROR_CODES = new Set([
  "auth/user-token-expired",
  "auth/invalid-user-token",
  "auth/network-request-failed",
  "auth/too-many-requests",
]);

const shouldKeepCurrentSession = (error: unknown) => {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: string }).code)
      : "";
  return RECOVERABLE_AUTH_ERROR_CODES.has(code);
};

// FIX: Never force-throw on recoverable errors — always try to keep session
const syncFirebaseUserSession = async (currentUser: User) => {
  let resolvedUser = currentUser;
  let idToken: string | null = null;

  try {
    await currentUser.getIdToken(true);
  } catch (refreshErr) {
    console.warn(
      "useAuth: forced token refresh skipped:",
      (refreshErr as { code?: string })?.code,
    );
    // FIX: Don't throw here at all — just skip the force refresh
    // and try to use the existing token below
  }

  try {
    await currentUser.reload();
    resolvedUser = auth.currentUser ?? currentUser;
  } catch (reloadErr) {
    if (!shouldKeepCurrentSession(reloadErr)) {
      throw reloadErr;
    }
    console.warn(
      "useAuth: reload skipped while keeping session:",
      (reloadErr as { code?: string })?.code,
    );
    resolvedUser = auth.currentUser ?? currentUser;
  }

  try {
    // FIX: Use false (no force) — we already tried force above
    idToken = (await resolvedUser.getIdToken(false)) ?? null;
  } catch (tokenErr) {
    if (!shouldKeepCurrentSession(tokenErr)) {
      throw tokenErr;
    }
    console.warn(
      "useAuth: using existing session without fresh token:",
      (tokenErr as { code?: string })?.code,
    );
  }

  return { resolvedUser, idToken };
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

interface UserDetails {
  createdAt: Timestamp;
  email: string;
  firstName: string;
  hasNoLastName: boolean;
  isVerified: boolean;
  lastName: string;
  middleName: string;
  phone: string;
  phoneNumberVerified?: boolean;
  role: "student" | "admission-officer" | "counsellor" | string;
  updatedAt: Timestamp;
  userId: string;
  schoolName: string;
  branding: {
    accentColor: string;
    logoURL: string;
    updatedAt: string;
  };
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  const firestoreUnsubRef = useRef<(() => void) | null>(null);
  const lastKnownAuthenticatedUidRef = useRef<string | null>(null);
  const emailVerifiedRef = useRef(false);

  // FIX: Track whether we're in a pending verification flow
  // so we never treat a tab-switch as a logout during this window
  const pendingVerificationRef = useRef(false);

  const setEmailVerifiedSafe = (value: boolean) => {
    if (emailVerifiedRef.current && !value) return;
    emailVerifiedRef.current = value;
    setEmailVerified(value);
  };

  useEffect(() => startFirebaseIdTokenCookieSync(), []);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (currentUser) => {
      if (firestoreUnsubRef.current) {
        firestoreUnsubRef.current();
        firestoreUnsubRef.current = null;
      }

      if (currentUser) {
        lastKnownAuthenticatedUidRef.current = currentUser.uid;

        try {
          const { resolvedUser, idToken } =
            await syncFirebaseUserSession(currentUser);
          setUser(resolvedUser);
          setToken(idToken);
        } catch (sessionErr: any) {
          if (sessionErr?.code === "auth/user-disabled") {
            try {
              await signOut(auth);
            } catch (_) {}
            setLoading(false);
            return;
          }

          console.warn(
            "useAuth: continuing with existing Firebase session:",
            sessionErr?.code,
          );
          const fallbackUser = auth.currentUser ?? currentUser;
          setUser(fallbackUser);
          setToken(null);
        }

        const refreshedUser = auth.currentUser ?? currentUser;
        const userDocRef = doc(db, "users", refreshedUser!.uid);

        const unsubFirestore = onSnapshot(
          userDocRef,
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data() as UserDetails;
              setUserDetails(data);

              const isNowVerified =
                Boolean(auth.currentUser?.emailVerified) ||
                Boolean(data.isVerified);
              setEmailVerifiedSafe(isNowVerified);

              if (
                data.isVerified &&
                auth.currentUser &&
                !auth.currentUser.emailVerified
              ) {
                syncFirebaseUserSession(auth.currentUser)
                  .then(({ resolvedUser, idToken }) => {
                    setUser(resolvedUser);
                    if (idToken) setToken(idToken);
                    setEmailVerifiedSafe(true);
                  })
                  .catch((syncErr) => {
                    console.warn(
                      "useAuth: background verification sync warning:",
                      (syncErr as { code?: string })?.code,
                    );
                    setEmailVerifiedSafe(true);
                  });
              }
            } else {
              setUserDetails(null);
              setEmailVerifiedSafe(Boolean(auth.currentUser?.emailVerified));
            }
          },
          (err) => {
            console.error("Error listening to user details:", err);
            setUserDetails(null);
            setEmailVerifiedSafe(Boolean(auth.currentUser?.emailVerified));
          },
        );

        firestoreUnsubRef.current = unsubFirestore;
      } else {
        const hadAuthenticatedSession = Boolean(
          lastKnownAuthenticatedUidRef.current,
        );

        if (hadAuthenticatedSession) {
          // FIX: If we're in a pending verification flow, wait longer
          const recoveryMs = pendingVerificationRef.current ? 5000 : 3000;
          await sleep(recoveryMs);

          const recoveredUser = auth.currentUser;
          if (recoveredUser && !recoveredUser.isAnonymous) {
            try {
              const { resolvedUser, idToken } =
                await syncFirebaseUserSession(recoveredUser);
              setUser(resolvedUser);
              setToken(idToken);
              setEmailVerifiedSafe(Boolean(resolvedUser.emailVerified));
              setLoading(false);
              return;
            } catch (recoveryError) {
              console.warn(
                "useAuth: recovery after transient sign-out failed:",
                (recoveryError as { code?: string })?.code,
              );
            }
          }
        }

        emailVerifiedRef.current = false;
        pendingVerificationRef.current = false;
        lastKnownAuthenticatedUidRef.current = null;

        try {
          const anonymousUser = await ensureAnonymousFirebaseUser();
          const anonymousToken = anonymousUser
            ? await anonymousUser.getIdToken()
            : null;

          setUser(anonymousUser);
          setEmailVerified(false);
          setToken(anonymousToken);
          setUserDetails(null);
        } catch (error) {
          console.error("Anonymous auth failed:", error);
          setUser(null);
          setEmailVerified(false);
          setToken(null);
          setUserDetails(null);
        }
      }

      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (firestoreUnsubRef.current) {
        firestoreUnsubRef.current();
        firestoreUnsubRef.current = null;
      }
    };
  }, []);

  // FIX: Completely rewritten visibility/focus sync
  // Previously this called syncFirebaseUserSession which does getIdToken(true)
  // — a force refresh — which throws auth/user-token-expired and kills the session.
  useEffect(() => {
    if (!user || user.isAnonymous || emailVerified) return;

    let cancelled = false;

    const syncVerificationState = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.isAnonymous) return;

      try {
        // FIX: Reload the user first to check emailVerified status
        // without force-refreshing the token (which causes the crash)
        await currentUser.reload();
        const reloadedUser = auth.currentUser ?? currentUser;
        if (cancelled) return;

        const isNowVerified =
          Boolean(reloadedUser.emailVerified) ||
          Boolean(userDetails?.isVerified);

        if (isNowVerified) {
          // Only get a new token if verification status actually changed
          try {
            const idToken = await reloadedUser.getIdToken(false);
            if (!cancelled) setToken(idToken);
          } catch (tokenErr) {
            console.warn(
              "useAuth: token fetch after reload warning:",
              tokenErr,
            );
          }
          setUser(reloadedUser);
          setEmailVerifiedSafe(true);
        }
        // FIX: If not verified, do nothing — don't touch the session at all
      } catch (syncErr) {
        if (!cancelled) {
          console.warn(
            "useAuth: verification refresh warning:",
            (syncErr as { code?: string })?.code,
          );
          // FIX: Never sign out here — just warn and continue
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void syncVerificationState();
      }
    };

    window.addEventListener("focus", syncVerificationState);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", syncVerificationState);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [emailVerified, user, userDetails?.isVerified]);

  // FIX: Export setPendingVerification so verifyEmail() can signal
  // the hook to be extra careful about logout during verification flow
  const setPendingVerification = useCallback((isPending: boolean) => {
    pendingVerificationRef.current = isPending;
  }, []);

  // Memoize derived auth values to prevent unnecessary re-renders in consumers.
  // Without this, every setUser/setToken/setUserDetails call creates a new
  // return object, forcing all useAuth() consumers to re-render even if the
  // values they care about (isAnonymous, isVerified, etc.) haven't changed.
  const isAnonymous = user ? user.isAnonymous : true;
  const isVerified = emailVerified || (userDetails?.isVerified ?? false);

  return useMemo(
    () => ({
      user,
      userDetails,
      loading,
      token,
      isAnonymous,
      emailVerified,
      isVerified,
      setPendingVerification,
    }),
    [user, userDetails, loading, token, isAnonymous, emailVerified, isVerified, setPendingVerification],
  );
};
